import { useCallback, useMemo } from "react"
import { useBarGraphOption } from "./options"
import { useBarGraphModel } from "./model"
import { textStyle } from '../../../../../constants'
import { useGraphZoom } from "../../../components/zoom"
import { useStackLegend } from "../../../components/stackLegend"
import { useTooltip } from "../../../components/toolTip"
import { useGrid } from "../../../components/grid"
import { numericValueFormat, percentFormat } from "../../common"

type useBarGraphRenderOptionProps<T> = {
	height: number
	graphOption: ReturnType<typeof useBarGraphOption<T>>
	graphData: ReturnType<typeof useBarGraphModel<T>>
	graphZoom: ReturnType<typeof useGraphZoom>
}
export const useBarGraphRenderOption = <T,>({
	height,
	graphOption: {
    direction, configure,
		valueDef, stackDef, 
    is100, fixMax
	},
	graphData,
	graphZoom: {
    hasScroll,
    dataZoom
	}
}: useBarGraphRenderOptionProps<T>) => {
  const seriesLabelFormatter = useCallback(
    (params: {
      value?: number | null
    }) => {
      const value = params.value
      if (value === undefined || value === null) return ''
      if (is100) return percentFormat(null, value * 100, 1)
      return numericValueFormat(value, 1, valueDef?.unit)
    },
    [is100, valueDef]
  )
  const valueFormatter = useCallback(
    (value?: number | string | null) => {
      if (value === undefined || value === null) return ''
      if (is100) {
        if (typeof value === 'number') return percentFormat(null, value * 100, 1)
        return ''
      }
      return numericValueFormat(value, 1, valueDef?.unit)
    },
    [is100, valueDef]
  )

  const tooltip = useTooltip({ trigger: 'axis', valueDef, is100 })
  const legend = useStackLegend({ show: (configure.stack || stackDef) ? true : false })

  const grid = useGrid(direction === "horizontal" ? {
    verticalScroll: hasScroll,
    containLabel: true
  } : {
    topMargin: !is100,
    horizontalScroll: hasScroll,
    containLabel: true
  })

  const { yAxis, xAxis } = useMemo(
    () => {
      const categoryAxis = {
        type: 'category',
        inverse: direction === "horizontal" ? true : false,
        axisTick: { show: false },
        axisLabel: direction === "horizontal" ? {
          width: height / 2,
          align: 'right',
          overflow: 'truncate',
          ellipsis: '...',
          hideOverlap: false,
          showMinLabel: true
        } : {
          align: 'center',
          interval: 0
        },
        data: graphData?.data.map((it) => it.name) || []
      }
      const valueAxis = is100 ? {
        type: 'value',
        min: 0,
        max: 1,
        axisLabel: {
          show: false,
        }
      } : {
        type: 'value',
        min: 0,
        max: fixMax ? graphData?.max : undefined,
        axisLabel: {
          formatter: valueFormatter,
          align: direction === "horizontal" ? "right" : undefined
        }
      }
      return direction === "horizontal" ? {
        yAxis: categoryAxis,
        xAxis: valueAxis
      } : {
        yAxis: valueAxis,
        xAxis: categoryAxis
      }
    },
    [direction, graphData, height, valueFormatter, fixMax, is100]
  )

  const series = useMemo(
    () => graphData?.series.map(
      (series) => ({
        type: 'bar',
        stack: 'total',
        name: series.name,
        emphasis: {
          focus: 'none',
          blurScope: 'series'
        },
        label: {
          show: true,
          formatter: seriesLabelFormatter,
          position: 'inside'
        },
        data: graphData.data.map(
          (it) => ({
            key: it.code,
            name: it.name,
            value: is100 ? (
              it.total > 0 ? (it.data[series.code]?.value ?? 0) / it.total : 0
            ) : (
              it.data[series.code]?.value || '-'
            ),
            itemStyle: it.itemStyle
          })
        )
      })
    ),
    [graphData, seriesLabelFormatter, is100]
  )

	return {
		//animation: false,
		tooltip,
		grid,
		legend,
		dataZoom,
		yAxis,
		xAxis,
		series,
		textStyle
	}
}